import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../hooks/useAuth';
import { useTheme } from '../contexts/ThemeContext';
import { useKeyboardShortcut } from '../hooks/useKeyboardShortcut';

/**
 * CommandPalette — Cmd+K / Ctrl+K command palette.
 *
 * - Global shortcut opens/focuses the input.
 * - Esc closes.
 * - Arrow Up/Down navigates filtered list.
 * - Enter runs the highlighted command.
 * - Filter matches label + keywords (case-insensitive substring).
 *
 * Commands are grouped (Навигация / Действия). Items can be hidden via `hideIf(ctx)`
 * where ctx = { user }. Each command has `action(navigate, ctx)` where ctx =
 * { user, logout, toggleTheme, toggleDensity }.
 */
const COMMANDS = [
  { id: 'nav-overview',     group: 'Навигация', label: 'Перейти: Обзор',              keywords: 'overview kpi обзор', action: (nav) => nav('/overview') },
  { id: 'nav-sessions',     group: 'Навигация', label: 'Перейти: Сессии',             keywords: 'sessions visitors сессии', action: (nav) => nav('/sessions') },
  { id: 'nav-ai',           group: 'Навигация', label: 'Перейти: AI Консультант',     keywords: 'ai консультант', action: (nav) => nav('/ai-consultant') },
  { id: 'nav-settings',     group: 'Навигация', label: 'Перейти: Настройки виджета',  keywords: 'settings widget настройки', action: (nav) => nav('/settings') },
  { id: 'nav-domains',      group: 'Навигация', label: 'Перейти: Домены',             keywords: 'domains домены', action: (nav) => nav('/domains') },
  { id: 'nav-integrations', group: 'Навигация', label: 'Перейти: Интеграции',         keywords: 'integrations crm metrika интеграции', action: (nav) => nav('/integrations') },
  { id: 'nav-embed',        group: 'Навигация', label: 'Перейти: Код встраивания',    keywords: 'embed code встраивание', action: (nav) => nav('/embed') },
  { id: 'nav-billing',      group: 'Навигация', label: 'Перейти: Баланс',             keywords: 'billing money баланс', action: (nav) => nav('/billing') },
  { id: 'nav-quiz',         group: 'Навигация', label: 'Перейти: Квиз',               keywords: 'quiz constructor квиз', action: (nav) => nav('/quiz') },
  {
    id: 'nav-clients',
    group: 'Навигация',
    label: 'Перейти: Клиенты',
    keywords: 'clients admin клиенты',
    action: (nav) => nav('/clients'),
    hideIf: ({ user }) => user?.role !== 'admin' && !user?.isSuperadmin,
  },
  { id: 'action-logout',    group: 'Действия',  label: 'Выйти из системы',            keywords: 'logout exit выйти', action: (_nav, { logout }) => logout?.() },
  { id: 'action-theme',     group: 'Действия',  label: 'Переключить тему',            keywords: 'theme dark light тема', action: (_nav, { toggleTheme }) => toggleTheme?.() },
  { id: 'action-density',   group: 'Действия',  label: 'Переключить плотность',       keywords: 'density compact плотность', action: (_nav, { toggleDensity }) => toggleDensity?.() },
];

export function CommandPalette() {
  const [open, setOpen] = useState(false);
  const [query, setQuery] = useState('');
  const [sel, setSel] = useState(0);
  const inputRef = useRef(null);

  const navigate = useNavigate();
  const { user, logout } = useAuth();
  const { theme, setTheme, density, setDensity } = useTheme();

  const toggleTheme = useCallback(
    () => setTheme(theme === 'dark' ? 'light' : 'dark'),
    [theme, setTheme],
  );
  const toggleDensity = useCallback(
    () => setDensity(density === 'compact' ? 'comfortable' : 'compact'),
    [density, setDensity],
  );

  const openPalette = useCallback(() => setOpen(true), []);
  useKeyboardShortcut('mod+k', openPalette);

  useEffect(() => {
    if (open) {
      setQuery('');
      setSel(0);
      const t = setTimeout(() => inputRef.current?.focus(), 20);
      return () => clearTimeout(t);
    }
    return undefined;
  }, [open]);

  const ctx = useMemo(
    () => ({ user, logout, toggleTheme, toggleDensity }),
    [user, logout, toggleTheme, toggleDensity],
  );

  const visible = useMemo(() => COMMANDS.filter((c) => !(c.hideIf && c.hideIf(ctx))), [ctx]);

  const filtered = useMemo(() => {
    const q = query.trim().toLowerCase();
    if (!q) return visible;
    return visible.filter(
      (c) =>
        c.label.toLowerCase().includes(q) || (c.keywords || '').toLowerCase().includes(q),
    );
  }, [query, visible]);

  useEffect(() => {
    setSel(0);
  }, [query]);

  const run = useCallback(
    (cmd) => {
      if (!cmd) return;
      cmd.action(navigate, ctx);
      setOpen(false);
    },
    [navigate, ctx],
  );

  const onKey = (e) => {
    if (e.key === 'Escape') {
      e.preventDefault();
      setOpen(false);
    } else if (e.key === 'ArrowDown') {
      e.preventDefault();
      setSel((s) => Math.min(filtered.length - 1, s + 1));
    } else if (e.key === 'ArrowUp') {
      e.preventDefault();
      setSel((s) => Math.max(0, s - 1));
    } else if (e.key === 'Enter') {
      e.preventDefault();
      run(filtered[sel]);
    }
  };

  if (!open) return null;

  // Group filtered items preserving COMMANDS order.
  const groups = [];
  const groupMap = new Map();
  let flatIndex = 0;
  const indexMap = new Map(); // cmd.id -> flat index for highlight
  for (const cmd of filtered) {
    indexMap.set(cmd.id, flatIndex);
    flatIndex += 1;
    if (!groupMap.has(cmd.group)) {
      const bucket = { name: cmd.group, items: [] };
      groupMap.set(cmd.group, bucket);
      groups.push(bucket);
    }
    groupMap.get(cmd.group).items.push(cmd);
  }

  return (
    <div
      className="lk-cmdk-overlay"
      data-cmdk-overlay
      role="dialog"
      aria-modal="true"
      aria-label="Командная палитра"
      onMouseDown={(e) => {
        if (e.target === e.currentTarget) setOpen(false);
      }}
    >
      <div className="lk-cmdk-panel" data-cmdk-panel>
        <input
          ref={inputRef}
          className="lk-cmdk-input"
          type="text"
          value={query}
          onChange={(e) => setQuery(e.target.value)}
          onKeyDown={onKey}
          placeholder="Команда, раздел, действие…"
          aria-label="Поиск команды"
        />
        <div className="lk-cmdk-list" role="listbox">
          {filtered.length === 0 ? (
            <div className="lk-cmdk-empty">Ничего не найдено</div>
          ) : (
            groups.map((g) => (
              <div key={g.name}>
                <div className="lk-cmdk-group">{g.name}</div>
                {g.items.map((cmd) => {
                  const idx = indexMap.get(cmd.id);
                  const active = idx === sel;
                  return (
                    <div
                      key={cmd.id}
                      role="option"
                      aria-selected={active}
                      className={`lk-cmdk-item${active ? ' lk-cmdk-item--active' : ''}`}
                      onMouseEnter={() => setSel(idx)}
                      onMouseDown={(e) => {
                        e.preventDefault();
                        run(cmd);
                      }}
                    >
                      <span className="lk-cmdk-label">{cmd.label}</span>
                    </div>
                  );
                })}
              </div>
            ))
          )}
        </div>
        <div className="lk-cmdk-foot">
          <span>
            <kbd>↑</kbd>
            <kbd>↓</kbd> навигация
          </span>
          <span>
            <kbd>↵</kbd> выбрать
          </span>
          <span>
            <kbd>esc</kbd> закрыть
          </span>
        </div>
      </div>
    </div>
  );
}

export default CommandPalette;
